// App QueryClient + persisted (IndexedDB) query cache for offline use, and the offline outbox bootstrap.
import { QueryClient } from '@tanstack/react-query';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { PersistQueryClientProviderProps } from '@tanstack/react-query-persist-client';
import {
  QUERY_CACHE_KEY,
  applyOverlayToCache,
  clearPersistedQueryCache,
  createPersistedQueryStorage,
  flushOutbox,
  hasPendingOutboxEntries,
  isPersistableQueryKey,
  outbox,
  resolveOfflineIdentity,
  selectOfflineUserId,
  startOfflineSync,
  useAuthStore,
  userManager,
} from '@sportschool/shared-logic';
import { version as appVersion } from '../../package.json';

/** Persisted data older than this is dropped on restore. */
const PERSIST_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 7;

/**
 * Bump when a persisted response shape changes incompatibly (renamed/removed DTO fields, changed query keys):
 * a different buster discards the whole persisted cache on the next start.
 */
const PERSISTED_CACHE_SCHEMA = 1;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      // Must be >= maxAge: restored queries without an observer would otherwise be garbage-collected (and then
      // dropped from the persisted snapshot) after the default 5 minutes.
      gcTime: PERSIST_MAX_AGE_MS,
      // Show cached data and fire the first request even when the browser reports offline; failed retries pause
      // until the connection is back (no request spam, refetch on reconnect).
      networkMode: 'offlineFirst',
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const persister = createAsyncStoragePersister({
  key: QUERY_CACHE_KEY,
  // Entries are bound to the signed-in user: another user's cache is never restored (see queryPersistence.ts).
  storage: createPersistedQueryStorage(async () => (await resolveOfflineIdentity())?.userId ?? null),
  throttleTime: 1000,
});

export const persistOptions: PersistQueryClientProviderProps['persistOptions'] = {
  persister,
  maxAge: PERSIST_MAX_AGE_MS,
  buster: `${PERSISTED_CACHE_SCHEMA}:${appVersion}`,
  dehydrateOptions: {
    // Allow-list only (no billing / payments / admin lists / statistics) and only successful queries.
    shouldDehydrateQuery: (query) => query.state.status === 'success' && isPersistableQueryKey(query.queryKey),
    // Mutations are never persisted (coach offline writes go through the outbox).
    shouldDehydrateMutation: () => false,
  },
};

/** Re-applies pending outbox entries on top of the restored cache (also done per query by the overlay). */
export function onPersistedCacheRestored() {
  applyOverlayToCache(queryClient, outbox.all());
}

let started = false;

/** Starts the offline outbox engine and the auth-related cache housekeeping. Call once at startup. */
export function startOfflineRuntime() {
  if (started) return;
  started = true;

  // Installs the outbox cache overlay before the persisted cache is restored (restore runs after first render).
  startOfflineSync(queryClient);

  // Back online: the token is renewed first (silent renew), then unsent changes go out right away.
  userManager.events.addUserLoaded(() => {
    void hasPendingOutboxEntries().then((pending) => {
      if (pending) void flushOutbox({ force: true });
    });
  });

  // A different user in this tab (account switch, impersonation start/stop): drop the previous user's cache.
  let lastUserId = selectOfflineUserId(useAuthStore.getState());
  useAuthStore.subscribe((state) => {
    const userId = selectOfflineUserId(state);
    if (lastUserId && userId && userId !== lastUserId) {
      queryClient.clear();
      void clearPersistedQueryCache();
    }
    lastUserId = userId;
  });
}
